import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Minus, Plus, ShieldCheck, Truck, Clock, Lock, ArrowRight } from 'lucide-react';
import { FAQ_ITEMS } from '../data/mock';
import { useCatalog } from '../context/CatalogContext';
import { useCart } from '../context/CartContext';
import { useTranslation } from '../hooks/useTranslation';
import ProductCard from '../components/ProductCard';
import PaymentMethods from '../components/PaymentMethods';

export default function ProductDetail() {
  const { slug } = useParams();
  const { products, loading } = useCatalog();
  const { t, lang } = useTranslation();
  const product = products.find((p) => p.slug === slug);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [zoom, setZoom] = useState({ show: false, x: 50, y: 50 });
  const [selectedVariant, setSelectedVariant] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    setActiveImg(0);
    setQty(1);
    if (product?.hasAvailabilityOption && product.variants?.length) {
      const variants = product.variants;
      const best =
        variants.find((v) => v.disponibilite === 'stock' && v.available) ||
        variants.find((v) => v.disponibilite === 'precommande' && v.available) ||
        variants.find((v) => v.disponibilite === 'stock') ||
        variants[0];
      setSelectedVariant(best);
    } else {
      setSelectedVariant(product?.variants?.[0] || null);
    }
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-center">
        <div>
          <div className="font-serif text-3xl">{loading ? t('productDetail.loadingText') : t('productDetail.notFound')}</div>
          {!loading && <Link to="/montres" className="text-gold text-xs tracking-luxury uppercase mt-6 inline-block">{t('productDetail.backToAll')}</Link>}
        </div>
      </div>
    );
  }

  const related = products.filter((p) => p.collection === product.collection && p.slug !== product.slug).slice(0, 4);
  const faqItems = FAQ_ITEMS[lang] || FAQ_ITEMS.fr;
  const isAvailable = product.hasAvailabilityOption ? !!selectedVariant?.available : product.available;
  const displayPrice = selectedVariant?.price ?? product.price;
  const deliveryText = product.hasAvailabilityOption
    ? (selectedVariant?.disponibilite === 'precommande' ? t('productDetail.deliveryPreorder') : t('productDetail.deliveryInStock'))
    : t('productDetail.estimatedDelivery');

  const handleAdd = () => {
    addItem(product, qty, product.hasAvailabilityOption ? selectedVariant : null);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  const handleZoom = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoom({ show: true, x, y });
  };

  return (
    <main>
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-8 text-[11px] tracking-luxury uppercase text-[#888]">
        <Link to="/" className="hover:text-gold">{t('nav.accueil')}</Link>
        {product.collection && (
          <>
            <span className="mx-2">/</span>
            <Link to={`/collections/${product.collection}`} className="hover:text-gold capitalize">{product.collection.replace('-', ' ')}</Link>
          </>
        )}
        <span className="mx-2">/</span>
        <span className="text-[#1A1A1A]">{product.name}</span>
      </div>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 pt-8 pb-16 grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div>
          <div
            className="-mx-4 sm:mx-0 aspect-square bg-white overflow-hidden relative cursor-zoom-in"
            onMouseMove={handleZoom}
            onMouseLeave={() => setZoom({ show: false, x: 50, y: 50 })}
          >
            <img
              src={product.images[activeImg]}
              alt={product.name}
              className="w-full h-full object-contain object-center p-1.5 sm:p-4 md:p-6 transition-transform duration-500"
              style={zoom.show ? { transform: 'scale(1.8)', transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}}
            />
          </div>
          {product.images.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-4">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square bg-white overflow-hidden border transition-all ${activeImg === i ? 'border-black' : 'border-transparent hover:border-[#DDD5C4]'}`}
                >
                  <img src={img} alt={`Vue ${i + 1}`} className="w-full h-full object-contain object-center p-2" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:sticky lg:top-28 self-start">
          <div className={`text-[11px] tracking-luxury uppercase ${isAvailable ? 'text-gold' : 'text-red-600'}`} data-testid="product-availability">
            {isAvailable ? t('productDetail.inStock') : t('productDetail.outOfStock')}
          </div>
          <h1 className="font-serif text-4xl md:text-5xl font-light mt-3">{product.name}</h1>
          <div className="font-serif text-2xl mt-4 text-[#1A1A1A]">{displayPrice} €</div>
          <div className="text-xs text-[#777] tracking-luxury uppercase mt-2" data-testid="product-delivery-estimate">{deliveryText}</div>

          <p className="mt-8 text-[#555] leading-relaxed text-sm">{product.description}</p>

          {/* Availability variant selector (Shopify "Conditions de Livraisons" option) */}
          {product.hasAvailabilityOption && (
            <div className="mt-8">
              <div className="text-[11px] tracking-luxury uppercase text-[#666] mb-3">{t('productDetail.availability')}</div>
              <div className="flex flex-wrap gap-2">
                {['stock', 'precommande'].map((key) => {
                  const variant = product.variants.find((v) => v.disponibilite === key);
                  if (!variant) return null;
                  const isSelected = selectedVariant?.id === variant.id;
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedVariant(variant)}
                      disabled={!variant.available}
                      data-testid={`variant-option-${key}`}
                      className={`px-4 py-2.5 text-[11px] tracking-luxury uppercase border transition-colors ${
                        isSelected ? 'border-black bg-black text-white' : 'border-[#EAE4D8] text-[#666] hover:border-black hover:text-black'
                      } ${!variant.available ? 'opacity-40 cursor-not-allowed hover:border-[#EAE4D8] hover:text-[#666]' : ''}`}
                    >
                      {key === 'stock' ? t('productDetail.inStockOption') : t('productDetail.preorderOption')}
                      {!variant.available ? ` — ${t('productDetail.outOfStock')}` : ''}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="mt-10 flex items-center gap-6">
            <div className="text-[11px] tracking-luxury uppercase text-[#666]">{t('cart.quantity')}</div>
            <div className="flex items-center border border-[#EAE4D8]">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:bg-cream" aria-label="Diminuer"><Minus className="w-3 h-3" /></button>
              <span className="w-10 text-center text-sm">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="p-3 hover:bg-cream" aria-label="Augmenter"><Plus className="w-3 h-3" /></button>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {isAvailable ? (
              <>
                <Link
                  to="/panier"
                  onClick={() => addItem(product, qty, product.hasAvailabilityOption ? selectedVariant : null)}
                  data-testid="buy-now-button"
                  className="group w-full inline-flex items-center justify-center gap-4 px-8 py-6 bg-black text-white text-[12px] lg:text-[13px] tracking-[0.32em] uppercase font-medium hover:bg-[#333] transition-all duration-500 shadow-lg"
                >
                  {t('productDetail.buyNow')}
                  <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" />
                </Link>
                <button
                  onClick={handleAdd}
                  data-testid="add-to-cart-button"
                  className="w-full px-6 py-4 border border-black text-[11px] tracking-luxury uppercase hover:bg-black hover:text-white transition-all duration-500"
                >
                  {added ? t('productDetail.added') : t('productDetail.addToCart')}
                </button>
              </>
            ) : (
              <div data-testid="product-unavailable-notice" className="w-full px-6 py-5 bg-[#F5F1EA] text-center text-[11px] tracking-luxury uppercase text-[#888]">
                {t('productDetail.outOfStockNotice')}
              </div>
            )}
          </div>

          {/* Trust badges */}
          <div className="grid grid-cols-4 gap-4 mt-10 pt-8 border-t border-[#EAE4D8]">
            {[
              { icon: Truck, t: t('cart.badgeLivraison'), s: t('cart.badgeLivraisonSub') },
              { icon: ShieldCheck, t: t('cart.badgeGarantie'), s: t('cart.badgeGarantieSub') },
              { icon: Clock, t: t('cart.badgeExpedition'), s: t('cart.badgeExpeditionSub') },
              { icon: Lock, t: t('cart.badgePaiement'), s: t('cart.badgePaiementSub') }
            ].map((b, i) => (
              <div key={i} className="text-center">
                <b.icon className="w-5 h-5 text-gold mx-auto" strokeWidth={1.2} />
                <div className="text-[10px] tracking-luxury uppercase mt-2">{b.t}</div>
                <div className="text-[10px] text-[#888] mt-1">{b.s}</div>
              </div>
            ))}
          </div>

          {/* Payment */}
          <div className="mt-10">
            <PaymentMethods />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-ivory">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="text-[11px] tracking-luxury uppercase text-gold mb-3">{t('faq.label')}</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">{t('faq.title')}</h2>
          </div>
          <div className="divide-y divide-[#EAE4D8] border-y border-[#EAE4D8]">
            {faqItems.map((item, i) => (
              <details key={i} className="group py-6">
                <summary className="flex justify-between items-center cursor-pointer list-none font-serif text-lg md:text-xl hover:text-gold transition-colors">
                  {item.q}
                  <span className="text-gold text-2xl font-light transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 text-[#555] leading-relaxed text-sm">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-20 bg-cream">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <div className="text-[11px] tracking-luxury uppercase text-gold mb-3">{t('productDetail.relatedTitle')}</div>
              <h2 className="font-serif text-3xl md:text-4xl font-light">{t('collectionDetail.label')}</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
              {related.map((p) => <ProductCard key={p.slug} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 bg-[#1A1A1A] text-white text-center">
        <div className="font-serif text-4xl md:text-5xl font-light">{t('home.ctaTitle')}</div>
        <Link to="/collections" className="inline-flex items-center gap-3 mt-8 px-10 py-4 border border-white/70 text-[11px] tracking-luxury uppercase hover:bg-white hover:text-[#1A1A1A] transition-all duration-500">
          {t('home.viewAll')} <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </main>
  );
}
